import React, { useEffect, useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import uuid from 'react-uuid';
import { useDrawerContext } from 'components/Drawer/draggable-drawer/DraggableDrawer.context';
import { DRAWER_PADDING_OFFSET, POPOUT_PADDING_OFFSET } from 'components/Drawer/widget-drawer/WidgetDrawer.constants';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import SpreadsheetNote from 'components/SpreadsheetNotes/spreadsheet-note/SpreadsheetNote';
import useNoteSpreadsheet from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseNoteSpreadsheet/useNoteSpreadsheet';
import type { UseSpreadsheetNoteTabs } from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseSpreadsheetNoteTabs/useSpreadsheetNoteTabs.props';
import { getSheetReferenceCount } from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseSpreadsheetNoteTabs/useSpreadsheetNoteTabs.utils';
import {
  AlphaReferenceType,
  ColumnReferenceType,
  RowReferenceType,
  SpreadsheetNoteCellData,
  SpreadsheetNoteData,
} from 'components/SpreadsheetNotes/types';
import { Tab } from 'components/TabsNavigation';
import { useZoom } from 'context/ZoomContext';
import { EvaluatedCell } from 'models/interfaces/spreadsheet-types/core-types';
import { OnChangeFn } from 'models/interfaces/spreadsheet-types/workbook-types';
import { ApproachActionOption } from 'pages/ValuationsAllocation/components';
import type { Ordinate } from 'services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { NEGATIVE_PREFIX } from '../../../../pages/Financials/financials-widget-drawer/types';

const useSpreadsheetNoteTabs = ({
  notes,
  actionsAreDisabled,
  handleOnDeleteNote,
  handleMenuClose,
  handleOnAddNote,
  isAttributeMapperEnabled,
  attributeReferenceConfig,
  attributeMapper,
  documentId,
  displayOptions,
  parentContainerHeight,
}: UseSpreadsheetNoteTabs) => {
  const [mappingBadgeVersion, setMappingBadgeVersion] = useState(0);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const { spreadsheets } = useNoteSpreadsheet({
    notes,
    isAttributeMapperEnabled,
    attributeReferenceConfig,
    attributeManager: attributeMapper,
    isPreview: !!documentId,
    documentId,
    displayOptions,
  });

  const { drawerHeight, isPopped, popoutHeight } = useDrawerContext();
  const { zoomLevel } = useZoom();

  const effectiveHeight =
    parentContainerHeight ?? (isPopped && popoutHeight != null ? popoutHeight / zoomLevel : drawerHeight);
  const paddingOffset = isPopped ? POPOUT_PADDING_OFFSET : DRAWER_PADDING_OFFSET;
  const innerDrawerHeight = parentContainerHeight ?? effectiveHeight - paddingOffset;

  const { cells, onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets);

  const onNoteChange: OnChangeFn = (c, expr) => {
    onChange(c, expr);
    const cell = c as EvaluatedCell<SpreadsheetNoteCellData>;
    if (cell.data?.referenceType && cell.data.sheetName && isAttributeMapperEnabled && attributeMapper) {
      const docModPayload = {
        documentReferenceId: documentId,
        sheet: cell.data.sheetName,
      };
      if (cell.data.referenceType === AlphaReferenceType) {
        attributeMapper.handleOrderOfMagnitudeUpdate(docModPayload, Number(expr));
        setMappingBadgeVersion(currentVersion => currentVersion + 1);
      } else if (Array.isArray(expr)) {
        const values = expr
          .filter(value => value !== '')
          .map(value => ({
            value,
            direction: value.startsWith(NEGATIVE_PREFIX) ? 'negative' : 'positive',
          })) as Ordinate[];

        if (cell.data.referenceType === ColumnReferenceType) {
          attributeMapper.handleColumnUpdate(docModPayload, cell.columnLegend, values);
          setMappingBadgeVersion(currentVersion => currentVersion + 1);
        } else if (cell.data.referenceType === RowReferenceType) {
          attributeMapper.handleRowUpdate(docModPayload, cell.rowNumber, values);
          setMappingBadgeVersion(currentVersion => currentVersion + 1);
        }
      }
    }
  };

  // Tabs are cached here because legacy drawer consumers expect this hook to expose tab state.
  useEffect(() => {
    // Only allow a single temp note at any given time.
    const hasTempNote = !!notes?.find(i => i.temp_id);
    const noteTabs: Tab[] =
      spreadsheets
        ?.filter(sheet => sheet.initialObj)
        .map((sheet, i) => {
          const note = sheet.initialObj as SpreadsheetNoteData;
          const menuItems =
            handleOnDeleteNote && handleMenuClose
              ? [
                <ApproachActionOption<SpreadsheetNoteData, never>
                  key={`delete-${note.id ?? note.temp_id}`}
                  actionText="Delete"
                  argumentsList={[note]}
                  tooltipMessage="Delete Spreadsheet Note from the system"
                  onClick={() => {
                    handleOnDeleteNote({ ...note, is_deleted: true });
                    handleMenuClose();
                  }}
                />,
              ]
              : undefined;
          let tabId: string;
          if (note.id != null) {
            tabId = String(note.id);
          } else if (note.temp_id != null && note.temp_id !== '') {
            tabId = String(note.temp_id);
          } else if (documentId != null) {
            tabId = `xlsx-preview-${documentId}-${i}`;
          } else {
            tabId = `spreadsheet-note-tab-${i}`;
          }

          return {
            content: (
              <SpreadsheetNote
                key={note.id ?? note.temp_id ?? `${tabId}`}
                note={sheet}
                parentContainerHeight={innerDrawerHeight}
                isAttributeMapperEnabled={isAttributeMapperEnabled}
                onChange={onNoteChange}
                workbook={workbook}
                cells={cells}
                isDisabled={actionsAreDisabled}
                isXlsxPreview={!!documentId}
                xlsxPreviewOptions={documentId ? displayOptions : undefined}
              />
            ),
            disabled: actionsAreDisabled,
            id: tabId,
            label: note.name || `Spreadsheet Note ${i + 1}`,
            badge: isAttributeMapperEnabled
              ? getSheetReferenceCount(attributeMapper, note.name || '', documentId)
              : undefined,
            menuItems,
          };
        }) ?? [];

    if (handleOnAddNote) {
      noteTabs.push({
        id: 'add-button',
        content: <></>,
        disabled: false,
        isActionTab: true,
        label: (
          <Tooltip title={hasTempNote ? 'Only one pending note allowed' : ''}>
            <span>
              <Button onClick={handleOnAddNote} disabled={hasTempNote || actionsAreDisabled}>
                +
              </Button>
            </span>
          </Tooltip>
        ),
      });
    }

    setTabs(noteTabs);

    // exhaustive dependencies are not needed here and result in degraded performance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, actionsAreDisabled, spreadsheets, cells, innerDrawerHeight, displayOptions, mappingBadgeVersion]);

  return {
    tabs,
    workbookContextValue,
    onNoteChange,
    cells,
  };
};

export default useSpreadsheetNoteTabs;
